/**
 * @file Precomputed values and Padé approximant coefficients for e^x
 *
 * @internal
 */

import type { int, TwoF64 } from '../base/common.js';

/**
 * Coefficients (from lowest to highest degree) of the [n/n] Padé approximant
 * of `eˣ` (absolute values, ie. one vector represent both the numerator, where
 * all coefficients are positive, and denominator, where odd powers are negative
 * and even powers positive).
 *
 * @internal
 */
export const exp_pade = {
  12: [
    [1.0, 0.0],
    [0.5, 0.0],
    [0.11956521739130435, -3.620292471603771e-18],
    [0.018115942028985508, -6.536639184840143e-19],
    [0.0019409937888198758, 2.2896194946898852e-20],
    [0.00015527950310559007, -5.757719611646623e-21],
    [9.534706331045004e-6, -4.724260038385541e-22],
    [4.5403363481166683e-7, 1.7838425876940298e-23],
    [1.6692413044546575e-8, 7.193228367360906e-26],
    [4.6367814012629376e-10, -3.7461908184624405e-27],
    [9.273562802525876e-12, -7.2115866992630165e-28],
    [1.204358805522841e-13, -8.419012569145437e-31],
    [7.720248753351544e-16, 4.716798420047949e-33],
  ] as TwoF64[],
  13: [
    [1.0, 0.0],
    [0.5, 0.0],
    [0.12, 4.440892098500626e-18],
    [0.018333333333333333, -9.251858538542972e-20],
    [0.0019927536231884057, 8.422208180467107e-20],
    [0.00016304347826086958, -1.2821869170263357e-20],
    [1.0351966873706003e-5, 7.455292888959589e-22],
    [5.175983436853002e-7, -5.075182917917071e-24],
    [2.0431513566525008e-8, 6.703802664538289e-25],
    [6.306022705717595e-10, 1.5584695800716773e-26],
    [1.48377004840414e-11, 7.84850688789076e-28],
    [2.529153491597966e-13, 1.3378136740722885e-29],
    [2.8101705462199623e-15, -1.4060303479522783e-31],
    [1.5440497506703088e-17, 1.3269311328087899e-33],
  ] as TwoF64[],
  14: [
    [1.0, 0.0],
    [0.5, 0.0],
    [0.12037037037037036, 6.6818978333921455e-18],
    [0.018518518518518517, 1.02798428206033e-18],
    [0.002037037037037037, 1.824672100657086e-19],
    [0.00016975308641975308, 6.170582734763179e-21],
    [1.1070853462157809e-5, 8.443595421389728e-22],
    [5.751092707614447e-7, -5.639092131018967e-24],
    [2.3962886281726862e-8, -2.349621721257903e-25],
    [7.98762876057562e-10, 2.663378645218314e-26],
    [2.1020075685725316e-11, 1.3811363314332962e-27],
    [4.2464799365101653e-13, -2.0545471119971306e-29],
    [6.24482343604436e-15, 3.0110729340472513e-31],
    [6.004637919273424e-17, -3.7417884486890406e-33],
    [2.8593513901302015e-19, 1.5656456420592239e-35],
  ] as TwoF64[],
  15: [
    [1.0, 0.0],
    [0.5, 0.0],
    [0.1206896551724138, -5.263988478826173e-18],
    [0.01867816091954023, -1.4755119220952152e-18],
    [0.002075351213282248, -1.9385479456956657e-20],
    [0.0001756066411238825, 5.448704404508889e-21],
    [1.1707109408258833e-5, 2.503092340000192e-22],
    [6.271665754424375e-7, 2.853501159382782e-23],
    [2.7268111975758154e-8, -1.6364972787136693e-24],
    [9.640241607591267e-10, -6.412248419103649e-26],
    [2.754354745026076e-11, 1.0298262317230486e-27],
    [6.259897147786537e-13, -1.3312747776581447e-29],
    [1.0982275697871117e-14, -1.505189885132522e-31],
    [1.4079840638296303e-16, 3.127070052528893e-33],
    [1.1831798855711178e-18, 7.806761239132814e-35],
    [4.929916189879658e-21, -3.267258149588949e-37],
  ] as TwoF64[],
};

/**
 * Integer coefficients (from highest to lowest degree) of the [n/n] Padé
 * approximant of `eˣ` (absolute values, ie. one vector represent both the
 * numerator, where all coefficients are positive, and denominator, where odd
 * powers are negative and even powers positive).
 *
 * NB.
 *  - For n=16, coefficients need TwoF64 representation and don't produce better
 *    results than coefficients for n=15.
 *  - For n=15, coefficients produce accurate results for x in [-1, 1] with
 *    an average relative error less than u^2, and the maximum relative error
 *    observed is less than 10u^2.
 *  - Lower orders (12,13,14) produce accurate results as well but only for
 *    smaller ranges (x closer to zero).
 *
 * @internal
 */
export const exp_pade_int = {
  12: [1, 156, 12012, 600600, 21621600, 588107520, 12350257920, 201132771840, 2514159648000, 23465490048000, 154872234316800, 647647525324800, 1295295050649600] as int[],
  13: [1, 182, 16380, 960960, 40840800, 1323241920, 33522128640, 670442572800, 10559470521600, 129060195264000, 1187353796428800, 7771770303897600, 32382376266240000, 64764752532480000] as int[],
  14: [1, 210, 21840, 1485120, 73513440, 2793510720, 83805321600, 2011327718400, 38718058579200, 593676898214400, 7124122778572800, 64764752532480000, 420970891461120000, 1748648318376960000, 3497296636753920000] as int[],
  15: [1, 240, 28560, 2227680, 126977760, 5587021440, 195545750400, 5531151225600, 127216478188800, 2374707592857600, 35620613892864000, 420970891461120000, 3788738023150080000, 24481076457277440000, 101421602465863680000, 202843204931727360000] as int[],
};

/**
 * Integer coefficients (from highest to lowest degree) of the [n/n] Padé
 * approximant of `eˣ - 1`.
 *
 * NB. At the numerator, all even powers are zeros and the odd ones have two
 * times the corresponding coefficient for `eˣ`. At the denominator coefficients
 * are the same as those for `eˣ` (we don't reuse `exp_pade_int` because we need
 * higher order coefficients to produce accurate results, which don't fit in a
 * single f64).
 *
 * @internal
 */
export const expm1_pade_int = {
  15: [
    [
      [2.0, 0.0],
      [0.0, 0.0],
      [57120.0, 0.0],
      [0.0, 0.0],
      [2.5395552e8, 0.0],
      [0.0, 0.0],
      [3.910915008e11, 0.0],
      [0.0, 0.0],
      [2.544329563776e14, 0.0],
      [0.0, 0.0],
      [7.1241227785728e16, 0.0],
      [0.0, 0.0],
      [7.57747604630016e18, 0.0],
      [0.0, 0.0],
      [2.0284320493172736e20, 0.0],
      [0.0, 0.0],
    ],
    [
      [-1.0, 0.0],
      [240.0, 0.0],
      [-28560.0, 0.0],
      [2.22768e6, 0.0],
      [-1.2697776e8, 0.0],
      [5.58702144e9, 0.0],
      [-1.955457504e11, 0.0],
      [5.5311512256e12, 0.0],
      [-1.272164781888e14, 0.0],
      [2.3747075928576e15, 0.0],
      [-3.5620613892864e16, 0.0],
      [4.2097089146112e17, 0.0],
      [-3.78873802315008e18, 0.0],
      [2.448107645727744e19, 0.0],
      [-1.0142160246586368e20, 0.0],
      [2.0284320493172736e20, 0.0],
    ],
  ] as [TwoF64[], TwoF64[]],
  16: [
    [
      [0.0, 0.0],
      [544.0, 0.0],
      [0.0, 0.0],
      [6.51168e6, 0.0],
      [0.0, 0.0],
      [2.133226368e10, 0.0],
      [0.0, 0.0],
      [2.82703284864e13, 0.0],
      [0.0, 0.0],
      [1.696219709184e16, 0.0],
      [0.0, 0.0],
      [4.546485627780096e18, 0.0],
      [0.0, 0.0],
      [4.733008115073639e20, -32768.0],
      [0.0, 0.0],
      [1.2576278705767096e22, 65536.0],
      [0.0, 0.0],
    ],
    [
      [1.0, 0.0],
      [-272.0, 0.0],
      [36720.0, 0.0],
      [-3.25584e6, 0.0],
      [2.116296e8, 0.0],
      [-1.066613184e10, 0.0],
      [4.3020065088e11, 0.0],
      [-1.41351642432e13, 0.0],
      [3.816494345664e14, 0.0],
      [-8.48109854592e15, 0.0],
      [1.54355993535744e17, 0.0],
      [-2.273242813890048e18, 0.0],
      [2.652116616205056e19, 0.0],
      [-2.3665040575368194e20, 16384.0],
      [1.5213240369879552e21, 16384.0],
      [-6.288139352883548e21, -32768.0],
      [1.2576278705767096e22, 65536.0],
    ],
  ] as [TwoF64[], TwoF64[]],
  17: [
    [
      [2.0, 0.0],
      [0.0, 0.0],
      [93024.0, 0.0],
      [0.0, 0.0],
      [6.837264e8, 0.0],
      [0.0, 0.0],
      [1.79902090368e12, 0.0],
      [0.0, 0.0],
      [2.12027463648e15, 0.0],
      [0.0, 0.0],
      [1.190746235847168e18, 0.0],
      [0.0, 0.0],
      [3.076455274797865e20, 24576.0],
      [0.0, 0.0],
      [3.144069676441774e22, 1.212416e6],
      [0.0, 0.0],
      [8.300343945806283e23, 4.2074112e7],
      [0.0, 0.0],
    ],
    [
      [-1.0, 0.0],
      [306.0, 0.0],
      [-46512.0, 0.0],
      [4.6512e6, 0.0],
      [-3.418632e8, 0.0],
      [1.955457504e10, 0.0],
      [-8.9951045184e11, 0.0],
      [3.392439418368e13, 0.0],
      [-1.06013731824e15, 0.0],
      [2.756357027424e16, 0.0],
      [-5.95373117923584e17, 0.0],
      [1.0608466464820224e19, 0.0],
      [-1.5382276373989324e20, -12288.0],
      [1.7748780431526144e21, -24576.0],
      [-1.572034838220887e22, -606208.0],
      [1.0061022964613677e23, 524288.0],
      [-4.1501719729031416e23, -2.1037056e7],
      [8.300343945806283e23, 4.2074112e7],
    ],
  ] as [TwoF64[], TwoF64[]],
  18: [
    [
      [0.0, 0.0],
      [684.0, 0.0],
      [0.0, 0.0],
      [1.302336e7, 0.0],
      [0.0, 0.0],
      [6.919311168e10, 0.0],
      [0.0, 0.0],
      [1.54201791744e14, 0.0],
      [0.0, 0.0],
      [1.6538142164544e17, 0.0],
      [0.0, 0.0],
      [8.789872213708186e19, 0.0],
      [0.0, 0.0],
      [2.200848773509242e22, -409600.0],
      [0.0, 0.0],
      [2.2134250522150088e24, 1.12197632e8],
      [0.0, 0.0],
      [5.810240762064398e25, 3.482058752e9],
      [0.0, 0.0],
    ],
    [
      [1.0, 0.0],
      [-342.0, 0.0],
      [58140.0, 0.0],
      [-6.51168e6, 0.0],
      [5.372136e8, 0.0],
      [-3.459655584e10, 0.0],
      [1.79902090368e12, 0.0],
      [-7.7100895872e13, 0.0],
      [2.756357027424e15, 0.0],
      [-8.269071082272e16, 0.0],
      [2.083805912732544e18, 0.0],
      [-4.394936106854093e19, 0.0],
      [7.691138186994663e20, -36864.0],
      [-1.100424386754621e22, 204800.0],
      [1.2576278705767096e23, 4.849664e6],
      [-1.1067125261075044e24, -5.6098816e7],
      [7.055292353935341e24, -3.13458688e8],
      [-2.905120381032199e25, -1.741029376e9],
      [5.810240762064398e25, 3.482058752e9],
    ],
  ] as [TwoF64[], TwoF64[]],
  19: [
    [
      [2.0, 0.0],
      [0.0, 0.0],
      [143640.0, 0.0],
      [0.0, 0.0],
      [1.64745504e9, 0.0],
      [0.0, 0.0],
      [6.919311168e12, 0.0],
      [0.0, 0.0],
      [1.3531207225536e16, 0.0],
      [0.0, 0.0],
      [1.3428971437609728e19, 0.0],
      [0.0, 0.0],
      [6.812150965623844e21, 122880.0],
      [0.0, 0.0],
      [1.6600687891612566e24, 8.4148224e7],
      [0.0, 0.0],
      [1.646234882584913e26, 2.707554304e9],
      [0.0, 0.0],
      [4.299578163927655e27, -1.71824381952e11],
      [0.0, 0.0],
    ],
    [
      [-1.0, 0.0],
      [380.0, 0.0],
      [-71820.0, 0.0],
      [8.95356e6, 0.0],
      [-8.2372752e8, 0.0],
      [5.930838144e10, 0.0],
      [-3.459655584e12, 0.0],
      [1.67051941056e14, 0.0],
      [-6.765603612768e15, 0.0],
      [2.31533990303616e17, 0.0],
      [-6.714485718804864e18, 0.0],
      [1.6481010400702847e20, 6144.0],
      [-3.406075482811922e21, -61440.0],
      [5.8689300626913114e22, 1.703936e6],
      [-8.300343945806283e23, -4.2074112e7],
      [9.407056471913788e24, -6.0030976e7],
      [-8.231174412924564e25, -1.353777152e9],
      [5.229216685857959e26, -1.1611144192e10],
      [-2.1497890819638275e27, 8.5912190976e10],
      [4.299578163927655e27, -1.71824381952e11],
    ],
  ] as [TwoF64[], TwoF64[]],
  20: [
    [
      [0.0, 0.0],
      [840.0, 0.0],
      [0.0, 0.0],
      [2.422728e7, 0.0],
      [0.0, 0.0],
      [1.976946048e11, 0.0],
      [0.0, 0.0],
      [6.93908062848e14, 0.0],
      [0.0, 0.0],
      [1.220815585237248e18, 0.0],
      [0.0, 0.0],
      [1.1353584942706406e21, 20480.0],
      [0.0, 0.0],
      [5.533562630537522e23, 2.8049408e7],
      [0.0, 0.0],
      [1.3169879060679303e26, 5.60201728e9],
      [0.0, 0.0],
      [1.2898734491782964e28, 5.8403848192e11],
      [0.0, 0.0],
      [3.3536709678635706e29, 1.9583047041024e13],
      [0.0, 0.0],
    ],
    [
      [1.0, 0.0],
      [-420.0, 0.0],
      [87780.0, 0.0],
      [-1.211364e7, 0.0],
      [1.23559128e9, 0.0],
      [-9.88473024e10, 0.0],
      [6.425074656e12, 0.0],
      [-3.46954031424e14, 0.0],
      [1.5786408429792e16, 0.0],
      [-6.10407792618624e17, 0.0],
      [2.014345715641459e19, 1024.0],
      [-5.676792471353203e20, -10240.0],
      [1.3624301931247687e22, 245760.0],
      [-2.766781315268761e23, -1.4024704e7],
      [4.703528235956894e24, -3.0015488e7],
      [-6.584939530339651e25, -2.80100864e9],
      [7.408056971632108e26, -2.2175744e10],
      [-6.449367245891482e27, -2.9201924096e11],
      [4.084599255731273e28, -4.381110697984e12],
      [-1.6768354839317853e29, -9.791523520512e12],
      [3.3536709678635706e29, 1.9583047041024e13],
    ],
  ] as [TwoF64[], TwoF64[]],
};

/**
 * Maximum value for `n` in precomputed `exp_n`
 *
 * @internal
 */
export const exp_nmax = 80;

/**
 * Precomputed values of `e^n` for integer n in [-80..80]
 *
 * @internal
 */
export const exp_n = new Map<number, TwoF64>([
  [-80, [1.8048513878454153e-35, -1.2636603623764078e-51]],
  [-79, [4.906094730649281e-35, -2.5923014171574923e-51]],
  [-78, [1.3336148155022614e-34, -2.603844974828867e-51]],
  [-77, [3.6251409191435593e-34, -6.617945385725517e-51]],
  [-76, [9.854154686111257e-34, 5.579789539289405e-50]],
  [-75, [2.6786369618080778e-33, 1.2518186658451756e-49]],
  [-74, [7.281290178321643e-33, 3.726649567668615e-49]],
  [-73, [1.9792598779469045e-32, 5.055300371182672e-49]],
  [-72, [5.380186160021138e-32, 1.6916090256898304e-48]],
  [-71, [1.462486227251231e-31, 2.7014641081033153e-48]],
  [-70, [3.975449735908647e-31, -3.3648383059169216e-48]],
  [-69, [1.0806392777072785e-30, 6.225836066421075e-48]],
  [-68, [2.9374821117108028e-30, 1.3957530027117971e-46]],
  [-67, [7.984904245686979e-30, -3.431230873053941e-46]],
  [-66, [2.1705220113036395e-29, -5.402539683164098e-46]],
  [-65, [5.900090541597061e-29, 4.777297874743027e-45]],
  [-64, [1.603810890548638e-28, -7.361325221284214e-45]],
  [-63, [4.359610000063081e-28, 1.1989353030852367e-44]],
  [-62, [1.185064864233981e-27, 3.208491622889283e-44]],
  [-61, [3.221340285992516e-27, 1.5866022428836443e-43]],
  [-60, [8.75651076269652e-27, 3.013197648255243e-43]],
  [-59, [2.3802664086944007e-26, -6.273962924590865e-43]],
  [-58, [6.47023492564546e-26, 4.484917091791122e-42]],
  [-57, [1.7587922024243116e-25, 4.15980317660522e-42]],
  [-56, [4.780892883885469e-25, 2.5997617988035256e-41]],
  [-55, [1.2995814250075031e-24, -4.605270783786157e-41]],
  [-54, [3.532628572200807e-24, -8.094096176262617e-41]],
  [-53, [9.602680054508676e-24, 4.749703304222624e-40]],
  [-52, [2.6102790696677047e-23, 5.984495845470866e-40]],
  [-51, [7.095474162284704e-23, 4.7778488131925167e-39]],
  [-50, [1.9287498479639178e-22, -3.7546101071240096e-39]],
  [-49, [5.242885663363464e-22, 3.946311221816784e-39]],
  [-48, [1.4251640827409352e-21, -4.830736069847307e-38]],
  [-47, [3.873997628687187e-21, 3.3001112667909335e-37]],
  [-46, [1.0530617357553812e-20, 6.381524386895125e-37]],
  [-45, [2.8625185805493937e-20, -1.28946602004931e-37]],
  [-44, [7.781132241133797e-20, -9.437877624428643e-37]],
  [-43, [2.1151310375910805e-19, 2.2423105474681812e-36]],
  [-42, [5.74952226429356e-19, -9.021557247449317e-36]],
  [-41, [1.5628821893349888e-18, -2.5996555867082187e-35]],
  [-40, [4.248354255291589e-18, 1.2437470802645773e-34]],
  [-39, [1.1548224173015786e-17, -1.330695306484811e-34]],
  [-38, [3.1391327920480296e-17, 3.332818439440314e-34]],
  [-37, [8.533047625744066e-17, -7.39755982489535e-35]],
  [-36, [2.3195228302435696e-16, -2.4569853636432666e-32]],
  [-35, [6.305116760146989e-16, 1.6363666480723477e-32]],
  [-34, [1.713908431542013e-15, -3.3532575798999164e-32]],
  [-33, [4.658886145103398e-15, -3.729065338633055e-31]],
  [-32, [1.2664165549094176e-14, 1.858907962674809e-31]],
  [-31, [3.442477108469977e-14, -2.921947170555484e-30]],
  [-30, [9.357622968840175e-14, -2.1170146272646406e-30]],
  [-29, [2.543665647376923e-13, 1.591198648412177e-29]],
  [-28, [6.914400106940203e-13, -1.0161024051462307e-30]],
  [-27, [1.8795288165390832e-12, 5.596559509325792e-29]],
  [-26, [5.109089028063325e-12, -3.800638116652005e-28]],
  [-25, [1.3887943864964021e-11, -3.0169520590348644e-28]],
  [-24, [3.775134544279098e-11, 2.551313785803785e-28]],
  [-23, [1.026187963170189e-10, 1.2076075579263726e-27]],
  [-22, [2.7894680928689246e-10, 1.6924962566137554e-26]],
  [-21, [7.582560427911907e-10, 1.314355136798966e-26]],
  [-20, [2.061153622438558e-9, -4.19755767595054e-26]],
  [-19, [5.602796437537268e-9, -2.8597077682490063e-25]],
  [-18, [1.522997974471263e-8, -7.170963862158597e-25]],
  [-17, [4.139937718785167e-8, -1.7163831541032036e-24]],
  [-16, [1.1253517471925912e-7, -1.94396212385793e-24]],
  [-15, [3.059023205018258e-7, 2.475795293905549e-24]],
  [-14, [8.315287191035679e-7, 6.974174696131514e-24]],
  [-13, [2.2603294069810542e-6, 8.624211933289055e-23]],
  [-12, [6.14421235332821e-6, -4.775776004588108e-23]],
  [-11, [1.670170079024566e-5, 5.0144554767319223e-23]],
  [-10, [4.5399929762484854e-5, -2.637554055327531e-21]],
  [-9, [0.00012340980408667956, -1.1716659184174644e-20]],
  [-8, [0.00033546262790251185, -1.4402182510425795e-20]],
  [-7, [0.0009118819655545162, -3.574480397859321e-20]],
  [-6, [0.0024787521766663585, -6.768570073847724e-20]],
  [-5, [0.006737946999085467, 9.579094181215286e-20]],
  [-4, [0.01831563888873418, 1.6250688994271399e-18]],
  [-3, [0.049787068367863944, -1.4831389691394365e-18]],
  [-2, [0.1353352832366127, -1.042381423288669e-17]],
  [-1, [0.36787944117144233, -1.2428753672788363e-17]],
  [0, [1.0, 0.0]],
  [1, [2.718281828459045, 1.4456468917292502e-16]],
  [2, [7.38905609893065, -1.7971139497839148e-16]],
  [3, [20.085536923187668, -1.8275625525512858e-16]],
  [4, [54.598150033144236, 2.8741578015844115e-15]],
  [5, [148.4131591025766, 3.4863514900464198e-15]],
  [6, [403.4287934927351, 1.2359628024450387e-14]],
  [7, [1096.6331584284585, 9.869752640434095e-14]],
  [8, [2980.9579870417283, -2.7103295816873633e-14]],
  [9, [8103.083927575384, -2.1530877621067177e-13]],
  [10, [22026.465794806718, -1.3780134700517372e-12]],
  [11, [59874.14171519782, 1.7895764888916994e-12]],
  [12, [162754.79141900392, 5.30065881322063e-12]],
  [13, [442413.3920089205, 1.2118711752313224e-11]],
  [14, [1.2026042841647768e6, -1.5000525764327354e-11]],
  [15, [3.2690173724721107e6, -3.075806431120808e-11]],
  [16, [8.886110520507872e6, 5.321182483501564e-10]],
  [17, [2.41549527535753e7, -7.203995068362157e-10]],
  [18, [6.565996913733051e7, 1.4165536846555444e-9]],
  [19, [1.7848230096318725e8, 1.333018530234341e-8]],
  [20, [4.851651954097903e8, 4.880277289790406e-10]],
  [21, [1.3188157344832146e9, 8.043448618843281e-8]],
  [22, [3.584912846131592e9, -2.3519384005402157e-7]],
  [23, [9.744803446248903e9, -6.74501500127677e-7]],
  [24, [2.648912212984347e10, 7.670395527778119e-7]],
  [25, [7.200489933738588e10, -6.992440211033874e-6]],
  [26, [1.9572960942883878e11, -1.1364989227123904e-5]],
  [27, [5.3204824060179865e11, -2.8335783945658822e-5]],
  [28, [1.446257064291475e12, 7.602079742299693e-5]],
  [29, [3.931334297144042e12, 8.220112058084352e-5]],
  [30, [1.0686474581524463e13, -0.0007436345313492586]],
  [31, [2.9048849665247426e13, -0.0005501643178883202]],
  [32, [7.896296018268069e13, 0.007660978022635108]],
  [33, [2.1464357978591606e14, 0.002124297761531261]],
  [34, [5.834617425274549e14, 0.006402902734610391]],
  [35, [1.5860134523134308e15, -0.02187035537422534]],
  [36, [4.311231547115195e15, 0.22711342229285691]],
  [37, [1.1719142372802612e16, -0.6912270602088098]],
  [38, [3.1855931757113756e16, 0.22032867170129863]],
  [39, [8.659340042399374e16, 2.953606932719265]],
  [40, [2.3538526683702e17, -14.592100089250966]],
  [41, [6.398434935300549e17, 37.22266340351557]],
  [42, [1.739274941520501e18, 55.394681303611236]],
  [43, [4.727839468229346e18, 257.4744575627443]],
  [44, [1.2851600114359308e19, -12.1907003678569]],
  [45, [3.4934271057485095e19, 436.0347972334061]],
  [46, [9.496119420602448e19, 5929.133649117119]],
  [47, [2.5813128861900675e20, -15192.714199784727]],
  [48, [7.016735912097631e20, 30185.471599886117]],
  [49, [1.9073465724950998e21, -98786.90015904616]],
  [50, [5.184705528587072e21, 419031.45332293346]],
  [51, [1.4093490824269389e22, -614323.8566876298]],
  [52, [3.831008000716577e22, -661524.304512138]],
  [53, [1.0413759433029089e23, -7.520901270665062e6]],
  [54, [2.830753303274694e23, -4.711377645198593e6]],
  [55, [7.694785265142018e23, -3.868399744098706e7]],
  [56, [2.091659496012996e24, 5.079641515721467e7]],
  [57, [5.685719999335932e24, 2.0801558082063326e8]],
  [58, [1.545538935590104e25, 1.2092033491117463e8]],
  [59, [4.2012104037905144e25, -1.7624059056928084e9]],
  [60, [1.1420073898156842e26, 4.912247462314477e9]],
  [61, [3.10429793570192e26, 3.39761293411071e9]],
  [62, [8.438356668741454e26, 6.5719328084037315e10]],
  [63, [2.29378315946961e27, -7.566162968773138e10]],
  [64, [6.235149080811617e27, 1.3899738872492847e11]],
  [65, [1.6948892444103338e28, -6.61940830637628e11]],
  [66, [4.607186634331292e28, -2.37966706696394e12]],
  [67, [1.2523631708422137e29, 8.725173269482437e12]],
  [68, [3.404276049931741e29, -2.3689045745171566e13]],
  [69, [9.253781725587787e29, 4.1335361789492734e13]],
  [70, [2.515438670919167e30, 1.2647645018519611e14]],
  [71, [6.837671229762744e30, -2.8026814130938628e14]],
  [72, [1.8586717452841279e31, 9.331591492068494e14]],
  [73, [5.052393630276104e31, 2.4912292015869455e15]],
  [74, [1.3733829795401761e32, 8.003850355927158e15]],
  [75, [3.7332419967990015e32, 1.5538124781410182e16]],
  [76, [1.0148003881138887e33, -1.8313882415912828e16]],
  [77, [2.7585134545231703e33, -1.1490320660315066e17]],
  [78, [7.498416996990121e33, -4.7233584091570765e17]],
  [79, [2.0382810665126688e34, -5.7128185991424294e17]],
  [80, [5.54062238439351e34, 2.1811937023229343e18]],
]);
